// L'EFFACEUR — Onglet Zéro Trace (Données & Sécurité)
// CCC.6 — Refonte complète :
//   - Supprimé : Zone Admin Reset Test (déplacée vers E2ETestPanel admin)
//   - Supprimé : Reset Cache local V5.27 (outil dev, pas client)
//   - Fix : compteur "X document(s) · Y abonnement(s)" caché si == 0
//   - Fix : "Mission Bouclier" → "Mission Déménagement" (libellé à jour)
//   - Allégé : moins de blocs visuels, message plus clair selon état

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Trash2, AlertTriangle, Bell, Clock, CheckCircle, Loader2, FileCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { Button } from '../../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';

const DataSecurityTab = ({ user }) => {
  const [retentionStatus, setRetentionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    const fetchRetentionStatus = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/data-retention/status`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setRetentionStatus(res.data);
      } catch (error) {
        console.error('Error fetching retention status:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRetentionStatus();
  }, []);

  const handleFinalizeMission = async () => {
    setFinalizing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/data-retention/finalize-mission`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success('Mission finalisée. Vos données sensibles ont été supprimées.');
      setRetentionStatus((prev) => ({ ...prev, has_active_mission: false }));
      setShowConfirmDialog(false);
    } catch (error) {
      toast.error('Erreur lors de la finalisation');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  // Compteurs : on ne les affiche que s'il y a vraiment quelque chose
  const docsToDelete = retentionStatus?.documents_to_delete || 0;
  const subsDetected = retentionStatus?.subscriptions_detected || 0;
  const hasDataToDelete = docsToDelete > 0 || subsDetected > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-orbitron text-2xl font-bold text-white mb-2">Données & Sécurité</h2>
        <p className="text-gray-400 font-mono text-sm">// COFFRE_FORT_TEMPORAIRE</p>
      </div>

      {/* CCC.10 — Bloc valeur "Pourquoi Zéro Trace ?" — éducation client + cred légale */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-3"
      >
        <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
          <div className="text-cyan-400 text-xs font-mono uppercase mb-1">// PROMESSE</div>
          <p className="text-white text-sm font-bold mb-1">Vos données ne traînent pas</p>
          <p className="text-gray-400 text-xs leading-relaxed">
            Vos relevés bancaires, RIB, justificatifs sont supprimés automatiquement
            une fois la mission terminée. Aucune copie persistante.
          </p>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
          <div className="text-success text-xs font-mono uppercase mb-1">// CONSERVÉ</div>
          <p className="text-white text-sm font-bold mb-1">Mandats &amp; preuves légales</p>
          <p className="text-gray-400 text-xs leading-relaxed">
            On garde uniquement les pièces juridiquement nécessaires (mandats signés,
            rapports de résiliation) pendant 5 ans, comme l'exige le RGPD.
          </p>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
          <div className="text-magenta-400 text-xs font-mono uppercase mb-1">// CONTRÔLE</div>
          <p className="text-white text-sm font-bold mb-1">Vous décidez quand</p>
          <p className="text-gray-400 text-xs leading-relaxed">
            Vous pouvez forcer la suppression à tout moment via le bouton ci-dessous,
            avant le délai légal de rétention.
          </p>
        </div>
      </motion.div>

      {/* Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 border-2 border-cyan-400/30"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-cyan-400/20 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-orbitron text-lg font-bold text-white">Protocole Zéro Trace</h3>
            <p className="text-gray-400 text-sm">Chiffrement AES-256 · Suppression automatique programmée</p>
          </div>
        </div>

        {retentionStatus?.has_active_mission ? (
          <div className="space-y-4">
            {/* Statut du déclencheur */}
            <div
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                retentionStatus.is_terminated
                  ? 'bg-orange-400/10 border-orange-400/30'
                  : 'bg-gray-800/50 border-gray-700'
              }`}
            >
              <div
                className={`w-3 h-3 rounded-full flex-shrink-0 ${
                  retentionStatus.is_terminated ? 'bg-orange-400 animate-pulse' : 'bg-gray-600'
                }`}
              />
              <div>
                <p
                  className={`text-sm font-medium ${
                    retentionStatus.is_terminated ? 'text-orange-400' : 'text-gray-400'
                  }`}
                >
                  {retentionStatus.countdown_trigger}
                </p>
                {retentionStatus.date_cloture && (
                  <p className="text-gray-500 text-xs mt-0.5">
                    Clôture :{' '}
                    {new Date(retentionStatus.date_cloture).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                )}
              </div>
            </div>

            {/* Countdown */}
            {retentionStatus.countdown_started && (
              <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-400 text-sm">Suppression automatique dans</span>
                  <span
                    className={`font-orbitron text-2xl font-bold ${
                      (retentionStatus.days_remaining ?? 30) <= 7 ? 'text-orange-400' : 'text-cyan-400'
                    }`}
                  >
                    {retentionStatus.days_remaining ?? '—'} jours
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      (retentionStatus.days_remaining ?? 30) <= 7 ? 'bg-orange-400' : 'bg-cyan-400'
                    }`}
                    style={{
                      width: `${Math.max(
                        5,
                        ((retentionStatus.days_remaining ?? 30) / retentionStatus.retention_days) * 100
                      )}%`,
                    }}
                  />
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Date prévue :{' '}
                  {retentionStatus.deletion_scheduled
                    ? new Date(retentionStatus.deletion_scheduled).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })
                    : '—'}
                </p>
              </div>
            )}

            {!retentionStatus.countdown_started && (
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 text-center">
                <Clock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">Le compte à rebours démarre automatiquement</p>
                <p className="text-gray-500 text-xs mt-1">
                  dès que votre mission est marquée "Effacée" par notre équipe
                </p>
              </div>
            )}

            {/* Données concernées */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h4 className="text-red-400 font-bold text-sm mb-2 flex items-center gap-2">
                  <Trash2 className="w-4 h-4" /> Données supprimées
                </h4>
                <ul className="text-gray-400 text-sm space-y-1">
                  {(retentionStatus.deleted_items || []).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
                {/* CCC.6 : compteur uniquement si > 0, sinon message rassurant */}
                {hasDataToDelete ? (
                  <p className="text-gray-500 text-xs mt-3">
                    {docsToDelete > 0 && (
                      <>
                        <span className="text-red-400 font-bold">{docsToDelete}</span> document
                        {docsToDelete > 1 ? 's' : ''} à supprimer
                      </>
                    )}
                    {docsToDelete > 0 && subsDetected > 0 && ' · '}
                    {subsDetected > 0 && (
                      <>
                        <span className="text-red-400 font-bold">{subsDetected}</span> abonnement
                        {subsDetected > 1 ? 's' : ''} détecté{subsDetected > 1 ? 's' : ''}
                      </>
                    )}
                  </p>
                ) : (
                  <p className="text-gray-500 text-xs mt-3 italic">
                    Aucune donnée sensible en stockage à ce jour.
                  </p>
                )}
              </div>

              <div className="bg-success/10 border border-success/30 rounded-lg p-4">
                <h4 className="text-success font-bold text-sm mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Documents conservés
                </h4>
                <ul className="text-gray-400 text-sm space-y-1">
                  {(retentionStatus.preserved_items || []).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-success rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-gray-500 text-xs mt-3">Conservation légale : 5 ans (obligation RGPD)</p>
              </div>
            </div>

            {/* Email reminder info */}
            <div className="flex items-center gap-2 text-gray-400 text-sm bg-gray-900/30 rounded p-3">
              <Bell className="w-4 h-4 text-orange-400" />
              <span>Un email de rappel est envoyé 7 jours avant la suppression automatique.</span>
            </div>

            {/* Manual finalization button */}
            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
              <AlertDialogTrigger asChild>
                <Button className="w-full bg-gradient-to-r from-red-500/20 to-orange-500/20 border-2 border-red-500/50 text-red-400 hover:bg-red-500/30 hover:border-red-400 py-6 font-orbitron">
                  <Trash2 className="w-5 h-5 mr-2" />
                  Finaliser ma mission & détruire mes données maintenant
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-void border-2 border-red-500/50">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-red-400 font-orbitron flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Confirmation de Suppression
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-400">
                    Cette action est <strong className="text-red-400">irréversible</strong>. Vos relevés
                    bancaires, fichiers temporaires et données de scan seront définitivement supprimés.
                    <br />
                    <br />
                    <span className="text-success">Vos mandats signés et rapports PDF sont conservés</span>{' '}
                    conformément à nos obligations légales.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-gray-700 text-gray-400">Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleFinalizeMission}
                    disabled={finalizing}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {finalizing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Confirmer la suppression
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
            <h4 className="text-success font-orbitron text-lg mb-2">Aucune mission active</h4>
            <p className="text-gray-400 max-w-md mx-auto">
              Le coffre-fort temporaire est vide. Vos données seront chiffrées et supprimées
              automatiquement dès la fin de votre prochaine mission.
            </p>
          </div>
        )}
      </motion.div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass-card p-4 border border-gray-800">
          <h4 className="text-cyan-400 font-bold text-sm mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Délais de Rétention
          </h4>
          <ul className="text-gray-400 text-sm space-y-2">
            <li className="flex justify-between">
              <span>Mission Audit</span>
              <span className="text-cyan-400 font-mono">30 jours</span>
            </li>
            <li className="flex justify-between">
              <span>Mission Déménagement</span>
              <span className="text-cyan-400 font-mono">30 jours</span>
            </li>
            <li className="flex justify-between">
              <span>Mission Héritage</span>
              <span className="text-magenta-400 font-mono">60 jours</span>
            </li>
          </ul>
        </div>

        <div className="glass-card p-4 border border-gray-800">
          <h4 className="text-success font-bold text-sm mb-2 flex items-center gap-2">
            <Lock className="w-4 h-4" /> Sécurité des Données
          </h4>
          <ul className="text-gray-400 text-sm space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-success" />
              Chiffrement AES-256
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-success" />
              Serveurs sécurisés (UE)
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-success" />
              Conformité RGPD
            </li>
            <li className="flex items-center gap-2">
              <FileCheck className="w-3 h-3 text-success" />
              Mandats horodatés (art. 1367 C. civ.)
            </li>
          </ul>
        </div>
      </div>

      {/* CCC.6 — Note RGPD finale */}
      <div className="text-xs text-gray-500 text-center font-mono pt-2">
        // Pour exporter ou supprimer définitivement votre compte (droit RGPD), rendez-vous dans Mon Compte → Confidentialité.
      </div>

      {/* CCC.6 — Zone Admin Reset Test SUPPRIMÉE.
          Désormais accessible uniquement depuis Admin → Missions → "Outils de test E2E". */}
    </div>
  );
};

export { DataSecurityTab };
